import { getAnalyticsPublicConfig, type AnalyticsPublicConfig } from "@/lib/analytics-config";
import { updateTrackingConsent } from "@/lib/analytics-runtime";
import { hasCookieConsent } from "@/lib/cookie-consent";

export type AnalyticsEventName =
  | "site_cta_click" | "site_link_click" | "site_form_start" | "site_form_attempt"
  | "site_tool_use" | "site_tool_complete" | "site_search" | "site_scroll_depth" | "site_booking_open" | "site_booking_step"
  | "generate_lead" | "contact_form_submit" | "booking_confirmed" | "campaign_form_submit"
  | "campaign_cta_click" | "campaign_calculator_use" | "campaign_form_start"
  | "ai_kollen_cta" | "ai_kollen_booked" | "ai_kollen_email";

export type AnalyticsParams = {
  form_id?: string; method?: string; language?: string; booking_variant?: string;
  booking_step?: string; cta_location?: string; cta_type?: string; section_id?: string;
  link_type?: string; link_domain?: string; link_path?: string; tool_id?: string;
  campaign?: string; ad_group?: string; placement?: string; percent_scrolled?: number;
};

const tokenKeys = ["form_id", "method", "language", "booking_variant", "booking_step", "cta_location",
  "cta_type", "section_id", "link_type", "tool_id", "campaign", "ad_group", "placement"] as const;

/** Technical identifiers only: never labels, field values, email addresses or error messages. */
export function analyticsToken(value: string | undefined): string | undefined {
  return value && /^[a-zA-Z0-9_-]{1,80}$/.test(value) ? value : undefined;
}

/** URLs in our interaction events contain no queries, fragments or credentials. */
export function analyticsPath(value: string): string {
  try {
    const path = new URL(value, window.location.origin).pathname;
    return /@|%40/i.test(path) ? "/redacted" : path.slice(0, 300);
  } catch { return "/"; }
}

export function analyticsAllowed(): boolean {
  return typeof window !== "undefined" && process.env.NODE_ENV !== "development"
    && !/^\/(?:dev|api)(?:\/|$)/.test(window.location.pathname)
    && getAnalyticsPublicConfig().enabled && hasCookieConsent("analytics");
}

export function analyticsPageContext(): AnalyticsParams & { page_path: string } {
  const campaign = document.querySelector<HTMLElement>("[data-analytics-campaign]");
  return {
    page_path: analyticsPath(window.location.href),
    ...(analyticsToken(document.documentElement.lang) ? { language: document.documentElement.lang } : {}),
    ...(analyticsToken(campaign?.dataset.analyticsCampaign) ? { campaign: campaign!.dataset.analyticsCampaign } : {}),
    ...(analyticsToken(campaign?.dataset.analyticsAdGroup) ? { ad_group: campaign!.dataset.analyticsAdGroup } : {}),
  };
}

function technicalParams(params: AnalyticsParams): AnalyticsParams {
  const safe: AnalyticsParams = {};
  for (const key of tokenKeys) {
    const value = analyticsToken(params[key]);
    if (value) safe[key] = value;
  }
  if (params.link_domain && /^[a-zA-Z0-9.-]{1,253}$/.test(params.link_domain)) safe.link_domain = params.link_domain;
  if (params.link_path) safe.link_path = analyticsPath(params.link_path);
  if ([25, 50, 75, 90].includes(params.percent_scrolled ?? 0)) safe.percent_scrolled = params.percent_scrolled;
  return safe;
}

/** One GA event plus one GTM hook. GTM must not forward the hook to GA again. */
export function emitAnalyticsEvent(event: AnalyticsEventName, params: AnalyticsParams = {}, dataLayerEvent = event): boolean {
  if (!analyticsAllowed()) return false;
  const config = getAnalyticsPublicConfig();
  const payload = { ...analyticsPageContext(), ...technicalParams(params) };
  try {
    // Consent can predate lazy analytics mounting; retain the event without loading a vendor early.
    if (!window.gtag) updateTrackingConsent(true, config.gaMeasurementId);
    if (config.gaMeasurementId) window.gtag?.("event", event, {
      send_to: config.gaMeasurementId,
      // Override GA's implicit full URL on our custom hits as well as the explicit path.
      page_location: window.location.origin + payload.page_path,
      page_referrer: safeReferrer(),
      ...payload,
    });
  } catch { /* Tracking must not interrupt navigation or a saved lead. */ }
  try {
    if (config.gtmId) {
      window.dataLayer ??= [];
      // Clear optional context so GTM's data model cannot leak a previous campaign/form into this event.
      window.dataLayer.push(Object.fromEntries([...tokenKeys, "link_domain", "link_path", "percent_scrolled"].map(key => [key, null])));
      window.dataLayer.push({ event: dataLayerEvent, ...payload });
    }
  } catch { /* Vendor unavailable. */ }
  return true;
}

function safeReferrer(): string {
  try {
    const url = new URL(document.referrer);
    return url.origin; // Never forward arbitrary external paths, queries or fragments.
  } catch { return ""; }
}

export type GoogleAdsConversionKind = keyof AnalyticsPublicConfig["googleAdsConversions"];

/**
 * A confirmed lead: GA4 `generate_lead`, the GTM hook, Meta `Lead` and, when
 * the lead has one, its Google Ads conversion. Google Ads is a destination of
 * the site's Google tag, so `gtag("event", "conversion")` reaches it under the
 * same consent state; GTM must not add a second Ads tag for these hooks.
 */
export function emitConfirmedLead(
  params: AnalyticsParams,
  dataLayerEvent: AnalyticsEventName,
  googleAdsConversion?: GoogleAdsConversionKind,
): void {
  if (!emitAnalyticsEvent("generate_lead", params, dataLayerEvent)) return;
  const config = getAnalyticsPublicConfig();
  try {
    if (config.metaPixelId) window.fbq?.("track", "Lead");
  } catch { /* Vendor unavailable. */ }
  if (!googleAdsConversion || !config.googleAdsId) return;
  const { conversionLabel, value, currency } = config.googleAdsConversions[googleAdsConversion];
  try {
    window.gtag?.("event", "conversion", { send_to: `${config.googleAdsId}/${conversionLabel}`, value, currency });
  } catch { /* Vendor unavailable. */ }
}
